// Format an SDK error for user-facing display. Prefers the server's `{ detail }`
// body so we surface the real reason; falls back to the caller's generic
// message. Avoid concatenating `error.message` directly — the SDK ResponseError
// message embeds the full request URL and HTTP status, which reads as noise.
export async function describeSdkError(error: unknown, fallback: string): Promise<string> {
  const detail = await extractSdkErrorDetail(error).catch(() => undefined);
  return detail ?? fallback;
}

// Best-effort extraction of the `detail` message from an SDK ResponseError body.
// The V3 SDK throws errors that carry the original Response on `error.response`; most
// backend errors serialize as `{ detail: "..." }`. We clone the response before reading
// so the original body stream stays intact for anyone else inspecting the error.
export async function extractSdkErrorDetail(error: unknown): Promise<string | undefined> {
  if (!isRecord(error) || !(error.response instanceof Response)) return undefined;
  try {
    const body: unknown = await error.response.clone().json();
    if (isRecord(body) && typeof body.detail === "string") return body.detail;
  } catch {
    // non-JSON body; fall through.
  }
  return undefined;
}

// Synchronous HTTP status extraction — duck-types `{ response: { status: number } }`
// which is the shape thrown by both the V2 and V3 SDKs.
export function getResponseStatus(error: unknown): number | undefined {
  if (!isRecord(error) || !isRecord(error.response)) return undefined;
  const { status } = error.response;
  return typeof status === "number" ? status : undefined;
}

function readResponseStatusFromChain(error: unknown, maxDepth = 4): number | undefined {
  let current: unknown = error;
  for (let depth = 0; depth < maxDepth; depth++) {
    const status = getResponseStatus(current);
    if (status !== undefined) {
      return status;
    }
    if (!isRecord(current) || !("cause" in current)) {
      break;
    }
    current = current.cause;
  }
  return undefined;
}

/** True when the error chain includes HTTP 401 (session expired / unauthenticated). */
export function isUnauthorizedResponse(error: unknown): boolean {
  return readResponseStatusFromChain(error) === 401;
}

/** True when the error chain includes HTTP 403 (authenticated but lacking permission). Walks up to four cause links. */
export function isForbiddenResponse(error: unknown): boolean {
  return readResponseStatusFromChain(error) === 403;
}

/** True for either 401 or 403 anywhere in the SDK error cause chain (depth capped at 4). */
export function isAccessDeniedResponse(error: unknown): boolean {
  const status = readResponseStatusFromChain(error);
  return status === 401 || status === 403;
}

/** Maps an SDK error to an access-denied status, for building field/query state unions. */
export function resolveAccessDeniedStatus(error: unknown): "unauthorized" | "forbidden" | "error" {
  if (isUnauthorizedResponse(error)) {
    return "unauthorized";
  }
  if (isForbiddenResponse(error)) {
    return "forbidden";
  }
  return "error";
}

// 408 (Request Timeout), 425 (Too Early), and 429 (Too Many Requests) are transient —
// a retry can succeed. Everything else in 4xx is a definitive client failure.
const TRANSIENT_CLIENT_STATUSES = new Set([408, 425, 429]);

export function isNonRetryableClientError(error: unknown): boolean {
  const status = readResponseStatusFromChain(error);
  if (status === undefined) return false;
  if (TRANSIENT_CLIENT_STATUSES.has(status)) return false;
  return status >= 400 && status < 500;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null;
}
